import axios from 'axios';
import * as Sentry from '@sentry/react';
import type { ActiveIngredient } from './sprayService';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export type ConsumableType = 'chemical' | 'ppe' | 'fertilizer' | 'diesel';

export interface ConsumableBase {
  _id: string;
  productName: string;
  consumableType: ConsumableType;
  needsReview: boolean;
  tenantId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Set when the Consumable was soft-deleted. Frontend renders "<name> (deleted)" suffix on ledger surfaces. */
  deletedAt?: string | null;
}

// Chemical rows hydrate with all chemical-specific fields. PPE/fertilizer/diesel
// rows return only the base shape until later tracks register their discriminators.
export interface ChemicalConsumable extends ConsumableBase {
  consumableType: 'chemical';
  lNumber?: string;
  activeIngredients?: ActiveIngredient[];
  category?: string;
  dosePer100L?: number;
  dosePer100LUnit?: 'ml' | 'g' | 'L' | 'kg';
  pestTreated?: string;
  formulation?: string;
  // PHI sub-document + label/MSDS PDF metadata
  phi?: { type: 'days' | 'petal_drop_80' | 'month_of_year'; value: string };
  labelPdf?: { url: string; originalName: string; size: number };
  msdsPdf?: { url: string; originalName: string; size: number };
}

export type Consumable = ConsumableBase | ChemicalConsumable;

/**
 * Type guard for narrowing a Consumable to its ChemicalConsumable variant.
 *
 * The union isn't fully discriminated (ConsumableBase.consumableType is the
 * broad ConsumableType union, not the non-chemical literals), so the compiler
 * can't narrow on a bare equality check. Use this guard whenever you need to
 * read chemical-specific fields (lNumber, activeIngredient, phi, etc.) off a
 * value typed as Consumable.
 */
export function isChemical(doc: Consumable): doc is ChemicalConsumable {
  return doc.consumableType === 'chemical';
}

export interface ListConsumablesParams {
  consumableType?: ConsumableType;
  needsReview?: boolean;
}

export async function listConsumables(params: ListConsumablesParams = {}): Promise<Consumable[]> {
  const q = new URLSearchParams();
  if (params.consumableType) q.set('consumableType', params.consumableType);
  if (params.needsReview != null) q.set('needsReview', String(params.needsReview));
  const url = `/api/v1/consumables${q.toString() ? `?${q.toString()}` : ''}`;
  try {
    const res = await axios.get<Consumable[]>(url, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export async function getConsumable(id: string): Promise<Consumable> {
  try {
    const res = await axios.get<Consumable>(`/api/v1/consumables/${id}`, { headers: authHeaders() });
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}
